import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@savvy-kit/lib/utils';
import PropTypes from 'prop-types';

import { DashboardSidebarItem } from './dashboard-sidebar-item';

type Item = {
  path?: string;
  icon?: ReactNode;
  chip?: ReactNode;
  info?: ReactNode;
  children?: Item[];
  title: string;
  disabled?: boolean;
  /** Extra routes that also mark this item active, matched on the pathname as a prefix. */
  activePaths?: readonly string[];
};

type DashboardSidebarSectionProps = {
  items: Item[];
  path: string;
  title: string;
} & ComponentProps<'div'>;

const renderNavItems = ({
  depth = 0,
  items,
  path,
}: {
  depth?: number;
  items: Item[];
  path: string;
}): React.ReactElement => (
  <div role="list">
    {items.reduce<React.ReactElement[]>(
      (acc, item) =>
        reduceChildRoutes({
          acc,
          item,
          depth,
          path,
        }),
      [],
    )}
  </div>
);

const reduceChildRoutes = ({
  acc,
  item,
  depth,
  path,
}: {
  acc: React.ReactElement[];
  depth: number;
  item: Item;
  path: string;
}): React.ReactElement[] => {
  const key = `${item.title}-${depth}`;
  const partialMatch = path.includes(item.path || '');
  // asPath can carry a query string, so alias matching compares pathnames.
  const pathname = path.split('?')[0] ?? '';
  const isActive =
    path === item.path ||
    (item.activePaths ?? []).some((candidate) => pathname === candidate || pathname.startsWith(`${candidate}/`));

  if (item.children) {
    acc.push(
      <DashboardSidebarItem
        active={isActive}
        chip={item.chip}
        depth={depth}
        disabled={item.disabled}
        icon={item.icon}
        info={item.info}
        key={key}
        open={partialMatch}
        path={item.path}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          items: item.children,
          path,
        })}
      </DashboardSidebarItem>,
    );
  } else {
    acc.push(
      <DashboardSidebarItem
        active={isActive}
        chip={item.chip}
        disabled={item.disabled}
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        path={item.path}
        title={item.title}
      />,
    );
  }

  return acc;
};

export function DashboardSidebarSection(props: DashboardSidebarSectionProps) {
  const { items, path, title, className, ...other } = props;

  return (
    <div className={cn('w-full py-2', className)} {...other}>
      <div className="ml-8 text-xs font-bold uppercase leading-[1.875rem] text-secondary">{title}</div>
      {renderNavItems({
        items,
        path,
      })}
    </div>
  );
}

DashboardSidebarSection.propTypes = {
  items: PropTypes.array.isRequired,
  path: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};
